/**
 * The words the dashboard uses for each platform switch, in one place: the switches page reads
 * them for its rows and confirms, and the overview notice reads them for its message. A `.ts`
 * rather than living beside the page, because a SPA `.tsx` exports components only.
 *
 * Keyed loosely by string rather than the core key union: the server owns the list, and a key
 * added there before this file catches up should render as itself rather than crash the page.
 */
export interface SwitchCopy {
  label: string;
  /** What stops, in a staff member's words. */
  gates: string;
  /** Spelled out in the confirm when disabling does more than the switch's name suggests. */
  alsoDisables?: string;
}

export const SWITCH_COPY: Record<string, SwitchCopy> = {
  opens: {
    label: "Opens",
    gates: "Opening a box, for every tenant.",
    alsoDisables:
      "A battle entry counts as an open, so joining and creating battles stops as well — even if the Battles switch is left enabled.",
  },
  claims: {
    label: "Claims",
    gates: "Claiming a revealed open, which is the debit. Reveals already shown stay unclaimed.",
    alsoDisables:
      "Shoppers who have already opened will hold an unclaimed reveal until this is enabled again. They are not charged in the meantime.",
  },
  battles: {
    label: "Battles",
    gates: "Creating and joining battles. Battles already underway play out and stay claimable.",
  },
  topups: {
    label: "Top-ups",
    gates: "Buying balance with money, through Stripe.",
  },
  orders: {
    label: "Withdrawals",
    gates: "Withdrawing items for fulfillment, marketplace buys included.",
  },
  order_cancels: {
    label: "Withdrawal cancels",
    gates:
      "A tenant cancelling a withdrawal for its shopper. Staff can still cancel from the fulfillment console.",
  },
  chat: {
    // Plural like every other label: the notice reads "… are disabled".
    label: "Chat messages",
    gates:
      "Sending storefront chat messages, for every tenant. History stays readable and staff can still moderate.",
  },
  forge: {
    // Plural like every other label: the notice reads "… are disabled".
    label: "Forge box edits",
    gates:
      "Creating and editing forge boxes, for every tenant. Existing forge boxes still open, and creators can still claim their earnings.",
  },
  procurement_buying: {
    label: "Procurement buying",
    gates: "The job that buys stock from vendors. It stops quietly rather than erroring.",
  },
  quickbooks: {
    label: "QuickBooks posting",
    gates: "The job that posts to QuickBooks. It stops quietly rather than erroring.",
  },
  stockx_scraping: {
    // Plural like every other label: the notice reads "… are disabled".
    label: "StockX page renders",
    gates:
      "The StockX bot's page jobs, and the product fetches and daily sourcing runs that create them. The bot gets no new pages, and pages it already claimed can still finish.",
  },
  fanatics_scraping: {
    label: "Fanatics scrapes",
    gates:
      "The hourly Fanatics Collect listing scrape (ScrapeFanatics). Listings stop updating and, while the complete scrape is also off, nothing marks sold listings inactive except the sales check.",
  },
  fanatics_scraping_v2: {
    label: "Complete Fanatics scrapes",
    gates:
      "The complete Fanatics Collect scrape, which reads every listing by price range. While it runs it is the only scrape that marks unseen listings inactive; the other scrape keeps writing but skips its sweep.",
  },
  stockx_brightdata: {
    // Plural like every other label: the notice reads "… are disabled".
    label: "Bright Data StockX renders",
    gates:
      "Rendering StockX pages through Bright Data. Every new page job goes to the StockX bot instead, and so do jobs still waiting for Bright Data, which the bot can take at once. A Bright Data render already running finishes.",
  },
};

export function copyFor(key: string): SwitchCopy {
  return SWITCH_COPY[key] ?? { label: key, gates: "No description for this switch yet." };
}

/** "Opens", "Opens and Claims", "Opens, Claims and Withdrawals" — for a one-line notice. */
export function labelList(keys: readonly string[]): string {
  const labels = keys.map((key) => copyFor(key).label);
  if (labels.length <= 1) return labels[0] ?? "";
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}
